/**
 * Copyright Highway9 Networks Inc.
 */
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { diagnosticsUeService, simulatorServices } from "~/services";
import { Simulator } from "~/types/simulator";
import type { RootState } from "..";
import { store } from "..";
import { createSocketPatchReducer } from "../events/socketPatch";
import Radio from "~/types/radio";
import { UE_Simulator } from "~/types/ueSimulator";
import { radioData } from "./radioSlice";
import { observeStoreChange } from "../utils";

export const simulatorsAdapter = createEntityAdapter<Simulator>();
export const radioSimulatorsAdapter = createEntityAdapter<Radio>();
export const ueSimulatorsAdapter = createEntityAdapter<UE_Simulator>();

interface SimulatorState {
  open: boolean;
  deleteOpen: boolean;
  loading: boolean;
  simulatorsData: EntityState<Simulator>;

  radioSimulatorsData: EntityState<Radio>;
  selectedId: string | null;
  openRadioSimulator: boolean;
  openDeleteRadioSimulator: boolean;
  selectedRadioSimulatorId: string | null;

  ueSimLoading: boolean;
  ueSimulatorsData: EntityState<UE_Simulator>;
}

const initialState: SimulatorState = {
  open: false,
  deleteOpen: false,
  loading: true,
  simulatorsData: simulatorsAdapter.getInitialState(),

  selectedId: null,
  selectedRadioSimulatorId: null,
  openRadioSimulator: false,
  openDeleteRadioSimulator: false,
  radioSimulatorsData: radioSimulatorsAdapter.getInitialState(),

  ueSimLoading: true,
  ueSimulatorsData: ueSimulatorsAdapter.getInitialState(),
};

export const fetchSimulator = createAsyncThunk("simulator/fetchSimulator", async () => {
  const data = await simulatorServices.getSimulators();
  return data;
});

export const fetchRadioSimulator = createAsyncThunk("simulator/fetchRadioSimulator", async (_, thunk) => {
  const state = thunk.getState() as RootState;
  const radios = radioData(state);
  const simulatedRadios = radios.filter((radio) => radio.isSimulatedRadio);
  return simulatedRadios;
});

export const fetchUESimulators = createAsyncThunk("simulator/fetchUESimulators", async () => {
  const data = await diagnosticsUeService.getDiagnosticsUe();
  return data;
});

setTimeout(() => {
  // fetch the radioSimulator whenever radios are fetched
  observeStoreChange(
    (state) => radioData(state),
    (radios: Radio[]) => {
      if (radios.length) store.dispatch(fetchRadioSimulator());
    },
  );
});

const simulatorSlice = createSlice({
  name: "simulator",
  initialState,
  reducers: {
    setOpen(state, action: PayloadAction<boolean>) {
      state.open = action.payload;
    },
    setDeleteOpen(state, action: PayloadAction<boolean>) {
      state.deleteOpen = action.payload;
    },
    /** Store selection identity only; resolve the entity via selectors. */
    setSelected(state, action: PayloadAction<string | null>) {
      state.selectedId = action.payload;
    },
    /** Store radio-simulator selection identity only; resolve via selectors. */
    setRadioSelected(state, action: PayloadAction<string | null>) {
      state.selectedRadioSimulatorId = action.payload;
    },
    setOpenRadioSimulator(state, action: PayloadAction<boolean>) {
      state.openRadioSimulator = action.payload;
    },
    setOpenDeleteRadioSimulator(state, action: PayloadAction<boolean>) {
      state.openDeleteRadioSimulator = action.payload;
    },
    applySocketPatchesSimulators: createSocketPatchReducer(simulatorsAdapter, (s) => s.simulatorsData),
    applySocketPatchesUeSimulators: createSocketPatchReducer(ueSimulatorsAdapter, (s) => s.ueSimulatorsData),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSimulator.fulfilled, (state, action) => {
        simulatorsAdapter.setAll(state.simulatorsData, action.payload);
        state.loading = false;
      })
      .addCase(fetchRadioSimulator.fulfilled, (state, action) => {
        radioSimulatorsAdapter.setAll(state.radioSimulatorsData, action.payload);
        state.loading = false;
      })
      .addCase(fetchUESimulators.fulfilled, (state, action) => {
        ueSimulatorsAdapter.setAll(state.ueSimulatorsData, action.payload);
        state.loading = false;
        state.ueSimLoading = false;
      });
  },
});

export const simulatorAction = simulatorSlice.actions;

export default simulatorSlice.reducer;

const simulatorEntitySelectors = simulatorsAdapter.getSelectors(
  (state: RootState) => state.simulator.simulatorsData,
);
const radioSimulatorEntitySelectors = radioSimulatorsAdapter.getSelectors(
  (state: RootState) => state.simulator.radioSimulatorsData,
);
const ueSimulatorEntitySelectors = ueSimulatorsAdapter.getSelectors(
  (state: RootState) => state.simulator.ueSimulatorsData,
);

export const simulatorData = simulatorEntitySelectors.selectAll;
export const simulatorEntities = simulatorEntitySelectors.selectEntities;

export const radioSimulatorData = radioSimulatorEntitySelectors.selectAll;
export const radioSimulatorEntities = radioSimulatorEntitySelectors.selectEntities;

export const ueSimulatorData = ueSimulatorEntitySelectors.selectAll;
export const ueSimulatorEntities = ueSimulatorEntitySelectors.selectEntities;

export const simulatorState = createSelector(
  [(state: RootState) => state.simulator.selectedId, simulatorEntities],
  (selectedId, entities): Simulator | null =>
    selectedId ? entities[selectedId] ?? null : null,
);

export const radioSimulatorState = createSelector(
  [(state: RootState) => state.simulator.selectedRadioSimulatorId, radioSimulatorEntities],
  (selectedId, entities): Radio | null =>
    selectedId ? entities[selectedId] ?? null : null,
);

export const simulatorOpen = (state: RootState) => state.simulator.open;
export const simulatorDeleteOpen = (state: RootState) => state.simulator.deleteOpen;
export const simulatorLoading = (state: RootState) => state.simulator.loading;

export const radioSimulatorOpen = (state: RootState) => state.simulator.openRadioSimulator;
export const radioSimulatorDeleteOpen = (state: RootState) =>
  state.simulator.openDeleteRadioSimulator;

export const ueSimulatorLoading = (state: RootState) => state.simulator.ueSimLoading;
